/**
 * Literature defaults for solubility (g/L @ 20°C) and density (g/cm³)
 * when DB columns are null. Keys are lowercase salt names / formula fragments.
 */

const SOLUBILITY: Record<string, number> = {
  'potassium nitrate': 316,
  kno3: 316,
  'calcium nitrate': 1290,
  'ca(no3)2': 1290,
  'magnesium nitrate': 710,
  'mg(no3)2': 710,
  'sodium nitrate': 921,
  nano3: 921,
  'zinc nitrate': 1840,
  'copper nitrate': 1250,
  'ammonium nitrate': 1900,
  'potassium monobasic phosphate': 230,
  'potassium phosphate monobasic': 226,
  kh2po4: 230,
  'potassium dibasic phosphate': 1590,
  k2hpo4: 1590,
  'ammonium monobasic phosphate': 374,
  'ammonium dibasic phosphate': 693,
  'calcium monobasic phosphate': 18,
  'magnesium sulfate': 710,
  mgso4: 710,
  'potassium sulfate': 120,
  k2so4: 120,
  'ammonium sulfate': 754,
  'copper sulfate': 320,
  cuso4: 320,
  'zinc sulfate': 965,
  znso4: 965,
  'iron sulfate': 265,
  'ferrous sulfate': 156,
  'iron ii sulfate': 265,
  feso4: 265,
  'manganese sulfate': 980,
  'calcium sulfate': 2.1,
  caso4: 2.1,
  'potassium chloride': 344,
  kcl: 344,
  'ammonium chloride': 383,
  nh4cl: 383,
  'iron edta': 100,
  'zinc edta': 50,
  'copper edta': 50,
  'manganese edta': 50,
  'mn edta': 50,
  'iron dtpa': 80,
  'iron eddha': 60,
  'potassium carbonate': 1120,
  k2co3: 1120,
  'calcium carbonate': 0.013,
  'magnesium carbonate': 0.18,
  'sodium borate': 47,
  borax: 47,
  'boric acid': 57,
  h3bo3: 57,
  'sodium molybdate': 840,
  'potassium citrate': 1770,
  'phosphoric acid': 1480,
  h3po4: 1480,
};

const DENSITY: Record<string, number> = {
  'potassium nitrate': 2.11,
  'calcium nitrate': 1.82,
  'magnesium nitrate': 1.46,
  'ammonium nitrate': 1.72,
  'magnesium sulfate': 1.68,
  'potassium sulfate': 2.66,
  'potassium chloride': 1.98,
  'ammonium chloride': 1.53,
  'ammonium sulfate': 1.77,
  'copper sulfate': 2.28,
  'zinc sulfate': 1.97,
  'ferrous sulfate': 1.9,
  'manganese sulfate': 2.95,
  'boric acid': 1.44,
  'potassium monobasic phosphate': 2.34,
  'potassium phosphate monobasic': 2.34,
  'phosphoric acid': 1.68,
  'calcium nitrate (ag grade)': 1.1,
};

function lookup(map: Record<string, number>, name: string, formula?: string | null): number | null {
  const n = name.toLowerCase().trim();
  const f = (formula || '').toLowerCase().trim();
  if (map[n] != null) return map[n];
  if (f && map[f] != null) return map[f];
  for (const [key, value] of Object.entries(map)) {
    if (n.includes(key) || (f && f.includes(key))) return value;
  }
  // Pattern defaults for solubility
  return null;
}

export function resolveSolubility(
  name: string,
  formula: string | null | undefined,
  dbValue: number | null | undefined
): number | null {
  if (dbValue != null && !Number.isNaN(Number(dbValue))) return Number(dbValue);
  const found = lookup(SOLUBILITY, name, formula);
  if (found != null) return found;
  const n = name.toLowerCase();
  if (n.includes('nitrate')) return 500;
  if (n.includes('chloride')) return 300;
  if (n.includes('sulfate') || n.includes('sulphate')) return 200;
  if (n.includes('phosphate')) return 100;
  if (n.includes('carbonate')) return 10;
  if (n.includes('edta') || n.includes('dtpa') || n.includes('eddha')) return 75;
  return null;
}

export function resolveDensity(
  name: string,
  formula: string | null | undefined,
  dbValue: number | null | undefined
): number | null {
  if (dbValue != null && !Number.isNaN(Number(dbValue))) return Number(dbValue);
  const found = lookup(DENSITY, name, formula);
  if (found != null) return found;
  // Common solid fertilizers ~1–2 g/cm³; avoid fake precision if unknown
  return null;
}

export function formatSolubility(value: number | null): string {
  if (value == null) return 'N/A';
  if (value >= 10000) return 'Miscible';
  // keep sensible decimals
  const n = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${n} g/L`;
}

export function formatDensity(value: number | null): string {
  if (value == null) return 'N/A';
  const n = Math.round(value * 100) / 100;
  // Liquids: g/mL ≈ g/cm³
  return `${n} g/mL`;
}
